import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../api/client";
import { useWallet } from "../wallet/context";
import { shortDid } from "../util";

/** Accept a credential the issuer's console offered (the offer link or QR it
 *  prints names /get_credential_offer/<vc_id>). The credential is already
 *  signed and bound to a DID; this wallet takes it only if that DID is its own. */
export function OfferPage() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const { wallet, identity, refresh, ready } = useWallet();
  const [offer, setOffer] = useState<any>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.GET("/get_credential_offer/{credential_id}", { params: { path: { credential_id: id }, query: { metadata: "true" } as any },  // contract gap: metadata is described but not declared as a parameter
      parseAs: "json" }).then((r) => (r.response.ok && r.data && typeof r.data === "object" ? setOffer(r.data) : setErr("This offer does not exist.")));
  }, [id]);

  if (!ready) return <p className="note">Loading…</p>;
  if (err) return <p className="error" role="alert">{err}</p>;
  if (!offer) return <p className="note">Loading the offer…</p>;
  const p = offer.credentialPreview;
  const issuer = typeof p.issuer === "string" ? p.issuer : p.issuer?.id;
  const subject = p.credentialSubject?.id;
  const types: string[] = Array.isArray(p.type) ? p.type : [p.type];
  const mine = identity && subject === identity.did;

  const accept = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.POST("/get_credential_offer/{credential_id}", { params: { path: { credential_id: id } },
        body: { subject_id: identity!.did } });
      if (!r.response.ok || !r.data) throw new Error((r.error as any)?.error ?? (r.error as any)?.message ?? "the issuer refused the claim");
      await wallet.addDelegation(r.data as any);
      await refresh();
      nav("/me");
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <div>
      <h1>A credential for you</h1>
      <div className="card" data-testid="offer">
        <b>{types.includes("AuthorisedAttester") ? "Authorised attester" : types.filter((t) => t !== "VerifiableCredential").join(", ")}</b>
        {p.credentialSubject?.role && <div>Role: {p.credentialSubject.role}</div>}
        {p.credentialSubject?.scope && <div>Scope: {[].concat(p.credentialSubject.scope).join(", ")}</div>}
        <div className="hint">From <span className="mono">{shortDid(issuer ?? "")}</span> to <span className="mono">{shortDid(subject ?? "")}</span></div>
      </div>
      {!identity ? <p style={{ marginTop: 16 }}><Link className="btn primary" to={`/me?return=/offer/${id}`}>Set up your keys first</Link></p>
        : !types.includes("AuthorisedAttester") ? <p className="note" style={{ marginTop: 16 }}>This wallet holds AuthorisedAttester credentials only.</p>
        : !mine ? <p className="error" style={{ marginTop: 16 }}>This credential was issued to {shortDid(subject ?? "")}, not to this wallet's identity.</p>
        : <div className="row" style={{ marginTop: 16 }}><button className="primary" disabled={busy} onClick={accept} data-testid="accept-offer">Accept into my wallet</button></div>}
      {err && <p className="error" role="alert">{err}</p>}
    </div>
  );
}
